/* eslint-disable  @typescript-eslint/no-explicit-any */
import type {PipeMiddlewareElement, PipeMiddlewareNext} from '../../middleware-pipe';
import type { MiddlewarePipeDetails } from '../../models/middleware-pipe-details.model';
import type { SearchTab } from '../../../../components/result-page/search-tab/search-tab';
import type { SearcherType } from '../../../../models/cose-api/types/searcher.type';
import type { AutocorrectEv } from '../../../../api/model/response/documents/public/Autocorrect';
import { callCoseApi } from '../api-calls/call-cose-api';
import { authenticationHeaders } from '../../../../components/guided/subcomponents/utils';
import type {CoseQueryBodyRequest} from '../../../../models/cose-api/request/query-body-request.model';
import type {DashboardMisspellings} from '../../../../components/result-page/search-tab/subcomponents/dashboard-tab-view/dashboard-misspellings/dashboard-misspellings';
import {EolResult} from '../../../../constants/eolResult';

export const reqResCoseBuildQueryAfterEvents: PipeMiddlewareElement<
    MiddlewarePipeDetails,
    any
> = async (
  details: MiddlewarePipeDetails,
  next: PipeMiddlewareNext<any>,
  middlewareDepth: number,
  middlewareArray: Array<PipeMiddlewareElement<MiddlewarePipeDetails, any>>
): Promise<any> => {
    const searchTab: SearchTab | DashboardMisspellings = details.context;
    const defaultBody:CoseQueryBodyRequest = {
      searcher: (searchTab.alternativeSearcherActive ? searchTab.alternativeSearcher : searchTab.tabName) as SearcherType,
      country: searchTab.country,
      language: searchTab.language,
      term: searchTab.query,
    };
    let autocorrectEv: AutocorrectEv = {
      totalHits: null,
      suggestion: '',
      isAutocorrect: false,
      hasHitsError: false,
    };

    let isAutocorrectBlocked = false;

    if (searchTab.apigeeTokenUrl && searchTab.apigeeAuth) {
      const headers = await authenticationHeaders({tokenUrl: searchTab.apigeeTokenUrl, apigeeBasicAuth: searchTab.apigeeAuth});

      if(headers['Authorization']) {
        const authHeader = headers['Authorization'];
        details.additionalData = { ...details?.additionalData, ...{ authHeader } };
      }
    }

    //Required body data
    details.middlewareRequest.requestBody = details.middlewareRequest
      .requestBody
      ? { ...details.middlewareRequest.requestBody, ...defaultBody }
      : defaultBody;

    //Spealcheck
    details.middlewareRequest.requestBody.forceTerm =
        details.additionalData?.skipAutocorrection ||
        !searchTab.runAutocorrect ||
        searchTab.skipAutocorrection;

    //Sorting
    details.middlewareRequest.requestBody.sort = details.additionalData?.sort;

    //Per-tab loading
    if(!searchTab.isVisible && searchTab.optimizedLoading){
      details.middlewareRequest.requestBody.sortOptions = {
        'fetch': false
      };
      details.middlewareRequest.requestBody.facetsOptions = {
        'fetch': false
      };
    }

    //Pagination
    const currentPage = searchTab.currentPage || 1;
    const offset = (currentPage - 1) * searchTab.perPage;
    const pagination = {
      limit: searchTab.optimizedLoading && !searchTab.isVisible ? searchTab.optimizedLoadingLimit : searchTab.perPage,
      offset,
    };
    details.middlewareRequest.requestBody.pagination = details.middlewareRequest
      .requestBody.pagination
      ? { ...pagination, ...details.middlewareRequest.requestBody.pagination }
      : pagination;

    if(searchTab.isDashboardView) {
      details.middlewareRequest.requestBody.pagination = {
        limit:0,
        offset:0
      };
      details.middlewareRequest.requestBody.sortOptions = {
        'fetch': false
      };
      details.middlewareRequest.requestBody.facetsOptions = {
        'fetch': false
      };
    }
    let response = await next(details);

    const suggestions = response.suggestions?.find(
      (item) => item.type === 'suggestion/spellcheck'
    );

    if(response.suggestions?.length && suggestions && response.stats.totalHits === 0) {
      details.middlewareRequest.requestBody.forceTerm = true;
      isAutocorrectBlocked = true;
      response = await next(details);
    }

    searchTab?.facetService?.setFacets(response.facetsOptions);

    if ((searchTab.isAutocorrectEndpoint && details.middlewareRequest.requestBody.pagination.offset === 0 && (!details.middlewareRequest.requestBody?.filters || details.middlewareRequest.requestBody?.filters?.length == 0)) && !details.middlewareRequest.requestBody.sort || (searchTab.isAutocorrectEndpoint && searchTab.isDashboardView)) {
      const pagination = {
        limit: 0,
        offset,
      };
      details.middlewareRequest.requestBody.forceTerm = true;
      details.middlewareRequest.requestBody.pagination = details
        .middlewareRequest.requestBody.pagination
        ? {
          ...details.middlewareRequest.requestBody.pagination,
          ...pagination,
        }
        : pagination;
      details.middlewareRequest.requestBody.sortOptions = {
        'fetch': false
      };
      details.middlewareRequest.requestBody.facetsOptions = {
        'fetch': false
      };
      await callCoseApi(details).then((result) => {
          return (autocorrectEv = {
            ...autocorrectEv,
            totalHits: result.stats.totalHits,
          });
      });
    }

    searchTab.reflectedState = {
      ...searchTab.reflectedState,
      ajaxIsLoading: false,
      hasError: false,
      count: response.stats.totalHits
    };

    searchTab.queryResult = { ...response, suggestion: isAutocorrectBlocked ? undefined : suggestions?.text};
    searchTab.queryResult.searcher = response.searcher;
    searchTab.queryResult.searchResults = {};
    searchTab.queryResult.searchResults.totalHits = response.stats.totalHits;

    const ev = {
      count: searchTab.queryResult?.stats?.totalHits,
      term: searchTab.queryResult?.term ?? searchTab.query,
      tab: searchTab.tabName,
      ajaxIsLoading: false,
    };

    searchTab.searchCompleted.emit({
      ...searchTab.reflectedState,
      ...ev,
      documents: response.documents,
    });

    const relatedProducts = response.suggestions.filter(item => item.type === 'suggestion/quicklink');

    // Fallback, if more quicklinks exist, because backend can not provide more dependable field or change quickling name for now
    if (relatedProducts.length > 1) relatedProducts.suggestions.filter(item => item.text === 'TeSensors');

    if (relatedProducts[0]?.text) {
      // Custom event created to support more,
      // Than only search-tab, as we have also Dashboard View
      const relatedProductEvent = new CustomEvent('search:SearchResultsRelatedProduct', { detail: { relatedProduct: relatedProducts[0]?.text }});

      window.dispatchEvent(relatedProductEvent);
    }
    let replacementsTotalHits = 0;
    if(searchTab.isSubstitutionTool) {
      replacementsTotalHits = searchTab.queryResult.documents.filter(doc => doc.productEolReplacementStatus && doc.productEolReplacementStatus === EolResult.SUBSTITUTE).length;
      
      searchTab.eolDiscontinuedResults = {
        documents: searchTab.queryResult.documents.filter(doc => doc.productEolReplacementStatus && doc.productEolReplacementStatus === EolResult.DISCONTINUED),
        documentsGroup: searchTab.queryResult.documents.filter(doc => doc.productEolReplacementStatus && doc.productEolReplacementStatus === EolResult.DISCONTINUED_GROUP),
        replacementsTotalHits: replacementsTotalHits
      };
    }

    if (currentPage === 1) {
      searchTab.aggregatedResults = [];
    }
    const queryResultDocuments = searchTab.isSubstitutionTool && replacementsTotalHits
      ? searchTab.queryResult.documents.filter(doc => !doc?.productEolReplacementStatus || (doc?.productEolReplacementStatus !== EolResult.DISCONTINUED && doc?.productEolReplacementStatus !== EolResult.DISCONTINUED_GROUP))
      : searchTab.queryResult.documents;
    
    if (details.additionalData?.skipAutocorrection) {
      searchTab.aggregatedResults = queryResultDocuments;
    } else {
      searchTab.aggregatedResults = [
        ...searchTab.aggregatedResults,
        ...queryResultDocuments,
      ];
    }


    if (searchTab.queryResult.suggestions?.length && suggestions && !isAutocorrectBlocked &&
      (autocorrectEv.totalHits !== null || autocorrectEv.hasHitsError)) {
      autocorrectEv = {
        ...autocorrectEv,
        suggestion: suggestions?.text,
        isAutocorrect: true,
      };

      searchTab.searchAutocorrect.emit({
        ...autocorrectEv,
      });
    }

    if (
      searchTab.queryResult.suggestion === searchTab.query &&
        searchTab.isAutocorrectEndpoint
    ) {
      autocorrectEv = {
        ...autocorrectEv,
        isAutocorrect: false,
      };

      searchTab.searchAutocorrect.emit({
        ...autocorrectEv,
      });
    }

    return new Promise((r) => r(response));
};
